import React, { SFC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Button,
    Dialog,
    DialogTitle,
    makeStyles,
    DialogContent,
    DialogActions,
    Grid,
    Checkbox,
    Typography,
    CardContent,
    ListItem,
    Card,
    CircularProgress,
    Tab,
    Tabs,
    TextField,
    InputAdornment,
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { Location } from '../../../shared/types/location'
import { DialogConfirmButton } from '../../../shared/components/dialogConfirmButton'
import { Form } from 'react-final-form'
import { NoData } from '../../../shared/components/NoData'
import { UserModel } from '../../../shared/types/user'
import { TabPanel } from '../../../shared/components/tabPanel'
import { useCalcHeightToBottomOfDialog } from '../../../utils/ui'
import SearchIcon from '@material-ui/icons/Search'
import { fetchUsers } from '../../../redux/actions/users'

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiFilledInput-root': {
            borderRadius: theme.spacing(0.5),
        },
        '& .MuiFilledInput-root::before': {
            border: 0,
        },
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
        },
        '& input[type=number]': {
            '-moz-appearance': 'textfield',
        },
        '& .DraftEditor-editorContainer': {
            maxHeight: 'calc(100vh - 272px)',
        },
    },
    form: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'hidden',
        flex: 1,
    },
    dialogActions: {
        padding: theme.spacing(1, 3, 3, 3),
    },
    search: {
        paddingLeft: theme.spacing(1),
    },
    list: {
        overflowY: 'auto',
        //marginTop: theme.spacing(3),
    },
    listItem: {
        margin: theme.spacing(1, 1, 2, 1),
        display: 'flex',
        '& .Mui-selected': {
            borderBottom: `3px solid ${theme.palette.primary.main}`,
            paddingBottom: theme.spacing(2) - 3,
        },
    },
    listContent: {
        flex: '1 0 auto',
        textAlign: 'left',
        padding: 0,
        '&:last-child': {
            padding: 0,
        },
    },
    listItemButton: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'start',
        padding: theme.spacing(2),
    },
    listContentWrapper: {
        display: 'flex',
        flexDirection: 'column',
    },
    progress: {
        textAlign: 'center',
    },
    dataInfoWrapper: {
        textAlign: 'center',
        paddingLeft: theme.spacing(1),
    },
    checkbox: {
        color: '#6100ED',
        marginBottom: '4px',
        paddingLeft: '0px',
        paddingBottom: '0px',
        paddingTop: '0px',
        paddingRight: '8px',
    },
    tab: {
        width: '50%',
    },
    content: {
        overflow: 'hidden',
    },
    input: {
        backgroundColor: '#f5f5f5',
        borderRadius: theme.shape.borderRadius,
        overflow: 'hidden',
        border: '1px solid #ddd',

        '& .MuiFilledInput-root': {
            backgroundColor: '#f5f5f5',
            borderRadius: theme.shape.borderRadius,
            paddingBottom: '6px',
            height: '40px',
            boxShadow: 'none',
            '&:before, &:after': {
                display: 'none',
            },
        },
        '& .MuiFilledInput-input': {
            padding: '17px 12px 10px 0px',
        },
        '& .MuiInputLabel-root': {
            top: '-7px',
            fontSize: '0.9rem',
            // color: '#9e9e9e',
        },
        '& .MuiInputAdornment-filled.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel)': {
            marginTop: '8px',
        },
    },
}))

interface LocationWhitelistEditDialogProps {
    open: boolean
    onClose(whitelist?: string[]): void
    initialModel: Location
}
const LocationWhitelistEditDialog: SFC<LocationWhitelistEditDialogProps> = ({ open, onClose, initialModel }) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const dispatch = useDispatch()
    const usersExecuted = useSelector((state: any) => state.users.items.executed)
    const [search, setSearch] = useState('')
    const users = useSelector((state: any) => state.users.items.value)
    const whitelist = useSelector((state: any) => initialModel.whitelist) || []
    const [checked, setChecked] = useState(new Array(users.length).fill(false))
    const [beforeSubmit, setBeforeSubmit] = useState<boolean>(true)
    const tenantId = useSelector((state: any) => state.login.value.user.tenantId)

    const whitelistUsers: UserModel[] = []

    const [tabIndex, setTabIndex] = useState<number>(0)

    const handleSearchChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value)
    }

    const isUserInWhitelist = (user: UserModel) => {
        if (whitelist.includes(user.id)) {
            whitelistUsers.push(user)
            return true
        } else {
            return false
        }
    }

    const handleWhitelistChange = (event: any, user: UserModel, pos: number) => {
        if (event.target.checked && !isUserInWhitelist(user)) {
            whitelist.push(user.id)
        } else {
            var i = whitelist.indexOf(user.id)
            if (i !== -1) {
                whitelist.splice(i, 1)
            }
        }

        const updatedCheckedState = checked.map((item, index) => (index === pos ? !item : item))
        setChecked(updatedCheckedState)
    }

    const filteredUsers = users.filter((user: UserModel) => {
        const searchLower = search.toLowerCase()

        const simpleMatches =
            user.firstName.toLowerCase().includes(searchLower) ||
            user.lastName.toLowerCase().includes(searchLower) ||
            user.email.toLowerCase().includes(searchLower) ||
            user.phone?.toLowerCase().includes(searchLower)

        const rfidMatches = user.rfids?.some((rfid) => rfid.rfid.toLowerCase().includes(searchLower)) ?? false

        const carMatches = user.cars?.some((car) =>
            (car.carLicensePlate ?? '').toLowerCase().includes(searchLower)
        );
        
        return simpleMatches || rfidMatches || carMatches;
    })

    const listHeightFill = useCalcHeightToBottomOfDialog('listWrapper')

    const onDialogClose = (whitelist?: string[]) => {
        onClose(whitelist)
        setBeforeSubmit(true)
    }

    useEffect(() => {
        dispatch(fetchUsers(search, tenantId))
    }, [dispatch, tenantId, search])

    return (
        <Dialog className={classes.dialog} open={open}>
            <DialogTitle>Edit Location Whitelist</DialogTitle>
            <Form
                onSubmit={async (model) => {
                    model = {
                        ...model,
                    }
                    onDialogClose(whitelist)
                }}
                render={({ handleSubmit }) => (
                    <form onSubmit={handleSubmit} noValidate className={classes.form}>
                        <DialogContent className={classes.content}>
                            <Grid container alignItems="flex-start" spacing={2}>
                                <Grid item xs={12}>
                                    <Grid item xs={10} className={classes.search}>
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            placeholder={t('users.search')}
                                            value={search}
                                            onChange={handleSearchChanged}
                                            className={classes.input}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={2} className={classes.progress}>
                                        {!usersExecuted && <CircularProgress size={30} />}
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Tabs
                                        value={tabIndex}
                                        onChange={(event: any, newValue: number) => setTabIndex(newValue)}
                                    >
                                        <Tab label="All users" color="primary" className={classes.tab} />
                                        <Tab label="Whitelist" className={classes.tab} />
                                    </Tabs>
                                    <Grid id="listWrapper" item xs={12} className={classes.list} ref={listHeightFill}>
                                        <TabPanel value={tabIndex} index={0}>
                                            {usersExecuted && filteredUsers.length === 0 ? (
                                                <div className={classes.dataInfoWrapper}>
                                                    <NoData text={t('users.noUsers')} />
                                                </div>
                                            ) : (
                                                filteredUsers.map((user: UserModel, index: number) => (
                                                    <Card key={user.id} className={classes.listItem} elevation={1}>
                                                        <ListItem>
                                                            <Grid container alignItems="center">
                                                                <Grid item xs={11}>
                                                                    <CardContent className={classes.listContent}>
                                                                        <Typography variant="h6">{`${user.firstName} ${user.lastName}`}</Typography>
                                                                        <Typography component="div" variant="caption">
                                                                            {user.email}
                                                                        </Typography>
                                                                    </CardContent>
                                                                </Grid>
                                                                <Grid item xs={1} style={{ textAlign: 'right' }}>
                                                                    <Checkbox
                                                                        className={classes.checkbox}
                                                                        checked={isUserInWhitelist(user)}
                                                                        onChange={(event: any) =>
                                                                            handleWhitelistChange(event, user, index)
                                                                        }
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </ListItem>
                                                    </Card>
                                                ))
                                            )}
                                        </TabPanel>
                                        <TabPanel value={tabIndex} index={1}>
                                            {usersExecuted && filteredUsers.length === 0 ? (
                                                <div className={classes.dataInfoWrapper}>
                                                    <NoData text={t('users.noUsers')} />
                                                </div>
                                            ) : (
                                                whitelistUsers.map((user: UserModel, index: number) => (
                                                    <Card key={user.id} className={classes.listItem} elevation={1}>
                                                        <ListItem>
                                                            <Grid container alignItems="center">
                                                                <Grid item xs={11}>
                                                                    <CardContent className={classes.listContent}>
                                                                        <Typography variant="h6">{`${user.firstName} ${user.lastName}`}</Typography>
                                                                        <Typography component="div" variant="caption">
                                                                            {user.email}
                                                                        </Typography>
                                                                    </CardContent>
                                                                </Grid>
                                                                <Grid item xs={1} style={{ textAlign: 'right' }}>
                                                                    <Checkbox
                                                                        className={classes.checkbox}
                                                                        checked={true}
                                                                        onChange={(event: any) =>
                                                                            handleWhitelistChange(event, user, index)
                                                                        }
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </ListItem>
                                                    </Card>
                                                ))
                                            )}
                                        </TabPanel>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions className={classes.dialogActions}>
                            <Button onClick={() => onDialogClose()} color="primary">
                                {t('common.cancel')}
                            </Button>
                            <DialogConfirmButton
                                label={t('common.save')}
                                onClick={() => {
                                    if (beforeSubmit) {
                                        setBeforeSubmit(false)
                                    }
                                }}
                            />
                        </DialogActions>
                    </form>
                )}
            />
        </Dialog>
    )
}

export default LocationWhitelistEditDialog
